import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const CostCalculator = ({ costs, participants = [], onUpdate }) => {
  const [costItems, setCostItems] = useState({
    meat: {
      items: [
        { name: 'Picanha', price: 0, quantity: 0, unit: 'kg' },
        { name: 'Maminha', price: 0, quantity: 0, unit: 'kg' },
        { name: 'Linguiça', price: 0, quantity: 0, unit: 'kg' },
        { name: 'Frango', price: 0, quantity: 0, unit: 'kg' }
      ]
    },
    beverages: {
      items: [
        { name: 'Cerveja', price: 0, quantity: 0, unit: 'un' },
        { name: 'Refrigerante', price: 0, quantity: 0, unit: 'un' },
        { name: 'Água', price: 0, quantity: 0, unit: 'un' },
        { name: 'Carvão', price: 0, quantity: 0, unit: 'kg' }
      ]
    },
    sides: {
      items: [
        { name: 'Pão de Alho', price: 0, quantity: 0, unit: 'un' },
        { name: 'Farofa', price: 0, quantity: 0, unit: 'kg' },
        { name: 'Saladas', price: 0, quantity: 0, unit: 'un' },
        { name: 'Queijo', price: 0, quantity: 0, unit: 'kg' }
      ]
    },
    equipment: {
      items: [
        { name: 'Churrasqueira', price: 0, quantity: 0, unit: 'un' },
        { name: 'Carvão', price: 0, quantity: 0, unit: 'kg' },
        { name: 'Descartáveis', price: 0, quantity: 0, unit: 'un' },
        { name: 'Gelo', price: 0, quantity: 0, unit: 'kg' }
      ]
    }
  });

  const [suggestions, setSuggestions] = useState({
    meatPerPerson: 0.4, // kg per person
    beveragePerPerson: 4, // units per person
    showSuggestions: true
  });

  useEffect(() => {
    calculateTotals();
  }, [costItems, participants]);

  const calculateTotals = () => {
    const categoryTotals = {};
    let grandTotal = 0;

    Object.keys(costItems)?.forEach(category => {
      const categoryTotal = costItems?.[category]?.items?.reduce((sum, item) => {
        return sum + (item?.price * item?.quantity);
      }, 0);
      
      categoryTotals[category] = categoryTotal;
      grandTotal += categoryTotal;
    });

    const updatedCosts = {
      ...categoryTotals,
      total: grandTotal,
      perPerson: participants?.length > 0 ? grandTotal / participants?.length : 0
    };

    onUpdate(updatedCosts);
  };

  const updateItemValue = (category, itemIndex, field, value) => {
    const numValue = field === 'name' ? value : parseFloat(value) || 0;
    
    setCostItems(prev => ({
      ...prev,
      [category]: {
        ...prev?.[category],
        items: prev?.[category]?.items?.map((item, index) => 
          index === itemIndex ? { ...item, [field]: numValue } : item
        )
      }
    }));
  };

  const addCustomItem = (category) => {
    setCostItems(prev => ({
      ...prev,
      [category]: {
        ...prev?.[category],
        items: [
          ...prev?.[category]?.items,
          { name: '', price: 0, quantity: 0, unit: 'un' }
        ]
      }
    }));
  };

  const removeItem = (category, itemIndex) => {
    setCostItems(prev => ({
      ...prev,
      [category]: {
        ...prev?.[category],
        items: prev?.[category]?.items?.filter((_, index) => index !== itemIndex)
      }
    }));
  };

  const applySuggestions = () => {
    const participantCount = participants?.length || 1;
    
    setCostItems(prev => ({
      ...prev,
      meat: {
        ...prev?.meat,
        items: prev?.meat?.items?.map(item => ({
          ...item,
          quantity: item?.name === 'Picanha' ? participantCount * 0.15 :
                   item?.name === 'Maminha' ? participantCount * 0.15 :
                   item?.name === 'Linguiça' ? participantCount * 0.1 :
                   item?.name === 'Frango' ? participantCount * 0.1 : item?.quantity
        }))
      },
      beverages: {
        ...prev?.beverages,
        items: prev?.beverages?.items?.map(item => ({
          ...item,
          quantity: item?.name === 'Cerveja' ? participantCount * 3 :
                   item?.name === 'Refrigerante' ? participantCount * 1 :
                   item?.name === 'Água' ? participantCount * 2 : item?.quantity
        }))
      }
    }));
  };

  const categories = [
    { key: 'meat', label: 'Carnes', icon: 'Beef', color: 'text-red-600' },
    { key: 'beverages', label: 'Bebidas', icon: 'Coffee', color: 'text-blue-600' },
    { key: 'sides', label: 'Acompanhamentos', icon: 'Salad', color: 'text-green-600' },
    { key: 'equipment', label: 'Equipamentos', icon: 'Wrench', color: 'text-gray-600' }
  ];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="DollarSign" size={20} className="text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Custo Total</p>
              <p className="text-xl font-bold text-foreground">
                R$ {costs?.total?.toFixed(2) || '0.00'}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
              <Icon name="Users" size={20} className="text-success" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Por Pessoa</p>
              <p className="text-xl font-bold text-foreground">
                R$ {costs?.perPerson?.toFixed(2) || '0.00'}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
              <Icon name="Calculator" size={20} className="text-warning" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Participantes</p>
              <p className="text-xl font-bold text-foreground">
                {participants?.length || 0}
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Quick Suggestions */}
      {suggestions?.showSuggestions && participants?.length > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-medium text-primary flex items-center gap-2">
                <Icon name="Lightbulb" size={18} />
                Sugestões Automáticas
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Baseado em {participants?.length} participantes
              </p>
              <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                <li>• {(participants?.length * 0.5)?.toFixed(1)}kg de carne total</li>
                <li>• {participants?.length * 3} cervejas</li>
                <li>• {participants?.length * 2} águas</li>
              </ul>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={applySuggestions}
                iconName="Wand2"
              >
                Aplicar
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSuggestions(prev => ({ ...prev, showSuggestions: false }))}
              >
                <Icon name="X" size={16} />
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Cost Categories */}
      {categories?.map((category) => (
        <div key={category?.key} className="bg-card rounded-lg border border-border">
          <div className="p-4 border-b border-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-muted rounded-lg flex items-center justify-center">
                  <Icon name={category?.icon} size={16} className={category?.color} />
                </div>
                <div>
                  <h3 className="font-medium text-foreground">{category?.label}</h3>
                  <p className="text-sm text-muted-foreground">
                    R$ {costs?.[category?.key]?.toFixed(2) || '0.00'}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => addCustomItem(category?.key)}
                iconName="Plus"
              >
                Adicionar Item
              </Button>
            </div>
          </div>
          
          <div className="p-4 space-y-3">
            {costItems?.[category?.key]?.items?.map((item, index) => (
              <div key={index} className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                <div className="md:col-span-2">
                  <Input
                    label="Item"
                    value={item?.name}
                    onChange={(e) => updateItemValue(category?.key, index, 'name', e?.target?.value)}
                    placeholder="Nome do item"
                  />
                </div>
                
                <div>
                  <Input
                    label="Preço Unitário"
                    type="number"
                    step="0.01"
                    value={item?.price}
                    onChange={(e) => updateItemValue(category?.key, index, 'price', e?.target?.value)}
                    placeholder="0.00"
                  />
                </div>
                
                <div>
                  <Input
                    label={`Quantidade (${item?.unit})`}
                    type="number"
                    step="0.1"
                    value={item?.quantity}
                    onChange={(e) => updateItemValue(category?.key, index, 'quantity', e?.target?.value)}
                    placeholder="0"
                  />
                </div>
                
                <div className="flex items-center gap-2">
                  <div className="text-sm font-medium text-foreground">
                    R$ {(item?.price * item?.quantity)?.toFixed(2)}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeItem(category?.key, index)}
                    className="text-destructive hover:bg-destructive/10"
                  >
                    <Icon name="Trash2" size={14} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      {/* Payment Split Options */}
      <div className="bg-muted/30 rounded-lg p-4">
        <h3 className="font-medium text-foreground mb-4 flex items-center gap-2">
          <Icon name="Users" size={18} />
          Divisão de Pagamento
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-card rounded-lg p-4 border border-border">
            <h4 className="text-sm font-medium text-foreground">Divisão Igual</h4>
            <p className="text-xs text-muted-foreground mb-2">
              Todos pagam o mesmo valor
            </p>
            <p className="text-lg font-bold text-primary">
              R$ {costs?.perPerson?.toFixed(2) || '0.00'} / pessoa
            </p>
          </div>
          
          <div className="bg-card rounded-lg p-4 border border-border">
            <h4 className="text-sm font-medium text-foreground">Por Contribuição</h4>
            <p className="text-xs text-muted-foreground mb-2">
              Baseado na contribuição de cada um
            </p>
            <p className="text-sm text-muted-foreground">
              Quem trouxe carne paga menos
            </p>
          </div>
          
          <div className="bg-card rounded-lg p-4 border border-border">
            <h4 className="text-sm font-medium text-foreground">Personalizada</h4>
            <p className="text-xs text-muted-foreground mb-2">
              Valor diferente para cada pessoa
            </p>
            <Button variant="outline" size="sm" className="mt-1">
              Configurar
            </Button>
          </div>
        </div>
      </div>
      {/* Quick Actions */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          onClick={() => {
            navigator.clipboard?.writeText(`Churrasco - Custo Total: R$ ${costs?.total?.toFixed(2)}\nPor pessoa: R$ ${costs?.perPerson?.toFixed(2)}`);
            alert('Resumo copiado!');
          }}
          iconName="Copy"
        >
          Copiar Resumo
        </Button>
        
        <Button
          variant="outline"
          onClick={() => setSuggestions(prev => ({ ...prev, showSuggestions: true }))}
          iconName="RefreshCw"
        >
          Recalcular Sugestões
        </Button>
      </div>
    </div>
  );
};

export default CostCalculator;